import { fetchTokenResult } from "@bindery/auth-consumer/browser";
import { useResource } from "@bindery/ui/hooks/use-resource";
import { createContext, useContext, type ReactNode } from "react";
import { fetchSession, type SessionState } from "./session";

const LOADING: SessionState = { status: "loading" };
const UNREACHABLE: SessionState = { status: "unreachable" };

const SessionContext = createContext<SessionState>(LOADING);

const browserDeps = {
  fetchTokenResult: () => fetchTokenResult(),
  fetcher: (input: string, init: RequestInit) => fetch(input, init),
};

const NO_DEPS: readonly unknown[] = [];

// One read per page load, shared through context: binding a reader is a full
// navigation through the broker, so the answer cannot change under a mounted
// page.
export function SessionProvider({ children }: { children: ReactNode }) {
  const { resource } = useResource(
    (signal) => fetchSession(browserDeps, signal),
    NO_DEPS,
  );
  const state =
    resource.status === "ready"
      ? resource.data
      : resource.status === "error"
        ? UNREACHABLE
        : LOADING;

  return (
    <SessionContext.Provider value={state}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}
